import type { TraceSidecar } from "../../../data/schemas";
import type {
  TraceGlyph,
  TraceScene,
  TraceScenePath,
  TraceScenePoint,
} from "./types";

/**
 * The Ruling 88 control's two labels.
 *
 * Here rather than in the view so the tests and the smoke name the same
 * strings the screen does.
 */
export const TRACE_OVERLAY_SHOW = "Show the drawn overlay";
export const TRACE_OVERLAY_HIDE = "Hide the drawn overlay";

/**
 * Doc 03 section 7.3, locked: enemy red, friendly and control measures blue,
 * boundaries and report lines black.
 *
 * Ruling 61 of 8 October 2026 fixed the order. A boundary is typed
 * "friendly control measure - boundary" in the sidecar, so the friendly test
 * matched first and all five boundaries and all five report lines on Trace 5
 * drew blue. Black was unreachable for that sidecar. The narrower rule now
 * resolves first.
 */
export function traceColorForType(type: string): TraceScenePoint["color"] {
  const normalized = type.trim().toLowerCase();
  if (normalized.includes("enemy")) return "red";
  if (normalized.includes("boundary") || normalized.includes("report line"))
    return "black";
  if (normalized.includes("friendly") || normalized.includes("control"))
    return "blue";
  return "black";
}

/**
 * The frame shapes the two map symbol files define, by the name a sidecar
 * writes.
 *
 * `DP1_ATL_-_Map_Symbols__slide_fix_.md` line 417: "The basic symbol for an
 * observation post is the triangle. The colour determines affiliation", and
 * line 419 gives red as hostile. Lines 258 and 261 give the diamond as the
 * hostile frame for land forces equipment.
 */
const FRAMES: Readonly<Record<string, TraceGlyph["frame"]>> = Object.freeze({
  diamond: "diamond",
  triangle: "triangle",
});

/**
 * The size amplifier, as dots above the frame.
 * `DP1_ATL_-_Map_Symbols__slide_fix_.md` lines 275 to 278.
 */
const AMPLIFIERS: Readonly<Record<string, number>> = Object.freeze({
  crew: 0,
  team: 0,
  squad: 1,
  section: 2,
  platoon: 3,
  troop: 3,
});

/**
 * Role icons. One for now, and it is two cues together:
 * `DP1_ATL_-_Map_Symbols__slide_fix_.md` line 369 gives the horizontal oval
 * as armour, the armour track; line 423 gives the single diagonal from the
 * lower-left to the upper-right as reconnaissance, the cavalry belt.
 */
const ICONS: Readonly<Record<string, TraceGlyph["icon"]>> = Object.freeze({
  "armoured reconnaissance": "armoured-reconnaissance",
  "armored reconnaissance": "armoured-reconnaissance",
});

function key(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/**
 * How the trace draws this mark, or null for a plain marker.
 *
 * Nothing is inferred from the mark's type or label. A drawing comes from the
 * four fields the sidecar carries and from the two map symbol files, and an
 * element neither defines is **named in `undefinedElements` and left out**,
 * which Ruling 67d requires: it is drawn as today's plain marker and reported.
 *
 * The outline is the one element that has a defensible default. A mark with a
 * frame but no `status` is drawn solid, because `Map_Symbols__002_.md` line
 * 499 makes the dash the marked case — Enemy Suspected or Templated — and
 * line 498 leaves Enemy Known as the plain solid line. The absence is still
 * reported rather than passed over.
 */
export function traceGlyphFor(point: {
  readonly echelon?: string;
  readonly frame?: string;
  readonly icon?: string;
  readonly status?: "confirmed" | "suspected";
}): TraceGlyph | null {
  const asked =
    point.echelon !== undefined ||
    point.frame !== undefined ||
    point.icon !== undefined ||
    point.status !== undefined;
  if (!asked) return null;

  const undefinedElements: string[] = [];
  const frame = FRAMES[key(point.frame)] ?? null;
  if (point.frame !== undefined && frame === null) {
    undefinedElements.push(`frame "${point.frame}"`);
  }
  if (point.frame === undefined) undefinedElements.push("no frame");

  const icon = ICONS[key(point.icon)] ?? null;
  if (point.icon !== undefined && icon === null) {
    undefinedElements.push(`icon "${point.icon}"`);
  }

  const dots = AMPLIFIERS[key(point.echelon)];
  if (point.echelon !== undefined && dots === undefined) {
    undefinedElements.push(`echelon "${point.echelon}"`);
  }
  if (point.status === undefined) undefinedElements.push("no status");

  return {
    amplifierDots: dots ?? 0,
    frame,
    icon,
    outline: point.status === "suspected" ? "dashed" : "solid",
    undefinedElements,
  };
}

function scenePoint(
  point: TraceSidecar["features"][number],
  kind: TraceScenePoint["kind"],
): TraceScenePoint {
  return {
    color: traceColorForType(point.type),
    glyph: traceGlyphFor(point),
    id: point.id,
    kind,
    label: point.label,
    type: point.type,
    x: point.x * 1000,
    y: point.y * 1000,
  };
}

function scenePath(phase: TraceSidecar["phases"][number]): TraceScenePath {
  return {
    bindItemId: phase.bindItemId,
    detail: phase.detail,
    id: phase.id,
    label: phase.label,
    markerIds: phase.markers,
    points: phase.path
      .map(([x, y]) => `${String(x * 1000)},${String(y * 1000)}`)
      .join(" "),
  };
}

export function buildTraceScene(sidecar: TraceSidecar): TraceScene {
  return {
    points: [
      ...sidecar.features.map((point) => scenePoint(point, "feature")),
      ...sidecar.markers.map((point) => scenePoint(point, "marker")),
    ],
    paths: sidecar.phases.map(scenePath),
  };
}
